import { path } from '#app/utils/pz/routes'
import {
	getNavigation,
	type GroupedNavigation,
	type NavigationContext,
	type NavigationItem,
} from './resolveNavigation'

type MenuBuildParams = {
	t: (key: any, ...args: any[]) => any
	ctx: NavigationContext
	navRoutes?: any[]
	navPaths?: any
}

type MarketingMenuBuildParams = MenuBuildParams & {
	signupHrefOverride?: string
}

type MarketingMenus = {
	marketing: NavigationItem[]
	tools: NavigationItem[]
	social: NavigationItem[]
	user: NavigationItem[]
}

type DashboardMenus = {
	entities: NavigationItem[]
	support: NavigationItem[]
	user: NavigationItem[]
}

function overrideSignupHref(
	items: NavigationItem[],
	signupHrefOverride?: string,
): NavigationItem[] {
	if (!signupHrefOverride) return items
	const signupPrefix = path('auth.signup')
	return items.map((item) =>
		item.href.startsWith(signupPrefix)
			? {
					...item,
					href: signupHrefOverride,
				}
			: item,
	)
}

export function buildMarketingMenus({
	t,
	ctx,
	navRoutes,
	navPaths,
	signupHrefOverride,
}: MarketingMenuBuildParams): MarketingMenus {
	const publicGroups = getNavigation(
		t,
		ctx,
		{ area: 'public', groups: ['marketing', 'tools', 'social'], byGroup: true },
		navRoutes,
		navPaths,
	) as GroupedNavigation

	const userGroups = getNavigation(
		t,
		ctx,
		{ area: 'user', groups: ['user'], byGroup: true },
		navRoutes,
		navPaths,
	) as GroupedNavigation

	return {
		marketing: publicGroups.marketing ?? [],
		tools: overrideSignupHref(publicGroups.tools ?? [], signupHrefOverride),
		social: publicGroups.social ?? [],
		user: userGroups.user ?? [],
	}
}

export function buildDashboardMenus({
	t,
	ctx,
	navRoutes,
	navPaths,
}: MenuBuildParams): DashboardMenus {
	const dashboardGroups = getNavigation(
		t,
		ctx,
		{ area: 'dashboard', groups: ['entities', 'support', 'user'], byGroup: true },
		navRoutes,
		navPaths,
	) as GroupedNavigation

	const userGroups = getNavigation(
		t,
		ctx,
		{ area: 'user', groups: ['user'], byGroup: true },
		navRoutes,
		navPaths,
	) as GroupedNavigation

	return {
		entities: dashboardGroups.entities ?? [],
		support: dashboardGroups.support ?? [],
		user: userGroups.user ?? [],
	}
}

export function buildAdminMenus({
	t,
	ctx,
	navRoutes,
	navPaths,
}: MenuBuildParams): GroupedNavigation {
	return getNavigation(
		t,
		ctx,
		{
			area: 'admin',
			groups: ['entities', 'creationAssistant', 'studios', 'support'],
			byGroup: true,
		},
		navRoutes,
		navPaths,
	) as GroupedNavigation
}
